import type {
  VocechatHistoryMessage,
  VocechatHistoryResult,
  VocechatServiceStatus,
  VocechatSendResult,
  VocechatUploadResult,
  VocechatViewUrlResult,
  VocechatWsFrame,
  VocechatWsMessageData,
} from '@digital-cs/shared';
import { getVocechatMessageType } from '@digital-cs/shared';
import { dcsLog } from './logger';
import { createImSocket, ImSocketError, IM_SOCKET_TIMEOUT_MS, type ImSocket } from './im-socket';

/** 原生 WebSocket 访客 IM 客户端。AI 链路仍由 ws-client.ts 使用 SSE。 */
type Handler = (payload: unknown) => void;

export interface VocechatInit {
  /** IM 访客 SDK REST 根，含 /im3/api。 */
  baseUrl: string;
  /** 第三方页面使用组件域下的桥接页；自有 H5 不配置，直接连接。 */
  socketBridgeUrl?: string;
  /** 客服入口 agent_guid（当前即 widget clientId）。 */
  agentId: string;
  /** 按商户稳定持久化的访客标识。 */
  visitorId: string;
  pageUrl: string;
  pageTitle?: string;
  pageTitleProvider?: () => string;
  language: string;
  clientIp?: string;
  hostTokenProvider?: () => string | null;
}

export const IM_EVENT = {
  status: 'im.status',
  message: 'im.message',
  typing: 'im.typing',
  read: 'im.read',
  conversationChanged: 'im.conversation-changed',
  unreadChanged: 'im.unread-changed',
  sessionExpired: 'im.session-expired',
} as const;

export interface ImStatus {
  online: boolean;
  agentName: string;
  channelTypeLabel: string;
  offline?: {
    guideText: string | null;
    qrUrl: string | null;
    serviceHours: string | null;
  };
}

export interface ImIncomingMessage {
  mid: number;
  seq?: number;
  conversationId?: number;
  sessionId?: string;
  clientMsgId?: string;
  fromUid: number;
  senderRole?: 'visitor' | 'agent';
  isVisitor: boolean;
  msgType: 'text' | 'image' | 'video' | 'file';
  text: string;
  fileKey?: string;
  fileMime?: string;
  fileSize?: number;
  fileName?: string;
  ts: number;
}

class HttpCodeError extends Error {
  constructor(
    public readonly httpStatus: number,
    public readonly code: number | string | null,
    msg: string,
  ) {
    super(msg);
    this.name = 'HttpCodeError';
  }
}

function isAuthError(e: unknown): boolean {
  if (!(e instanceof HttpCodeError)) return false;
  return e.httpStatus === 401 || e.code === 401 || e.code === 4002 || e.code === 4004;
}

const MAX_PAGE_TITLE_LENGTH = 255;
const RECONNECT_MAX_MS = 30_000;
const PING_INTERVAL_MS = 25_000;
const HISTORY_POLL_INTERVAL_MS = 2_500;

let miniProgramLifecycleInstalled = false;
function installMiniProgramLifecycle(): void {
  if (miniProgramLifecycleInstalled) return;
  const bridge = (window as unknown as {
    WeixinJSBridge?: { on: (name: string, handler: (event: { active: boolean }) => void) => void };
  }).WeixinJSBridge;
  if (!bridge) return;
  miniProgramLifecycleInstalled = true;
  bridge.on('onPageStateChange', ({ active }) => {
    if (active) window.dispatchEvent(new Event('dcs:im-page-active'));
  });
}

function readPageTitle(provider?: () => string, fallback?: string): string {
  try {
    const title = provider?.() ?? fallback ?? (typeof document !== 'undefined' ? document.title : '');
    return typeof title === 'string' ? title.trim().slice(0, MAX_PAGE_TITLE_LENGTH) : '';
  } catch {
    return '';
  }
}

function makeClientMsgId(): string {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    // fall through
  }
  return `msg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function messageTimestamp(value: number | string): number {
  if (typeof value === 'number') return value;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : Date.now();
}

export class VocechatTransportClient {
  private listeners = new Map<string, Set<Handler>>();
  private init: VocechatInit | null = null;
  private status: VocechatServiceStatus | null = null;
  private statusPromise: Promise<VocechatServiceStatus> | null = null;
  private statusRefreshPromise: Promise<VocechatServiceStatus> | null = null;
  private socket: ImSocket | null = null;
  private connectPromise: Promise<void> | null = null;
  private generation = 0;
  private recoveryPromise: Promise<void> | null = null;
  private historyRecoveryPromise: Promise<void> | null = null;
  private lifecycleInstalled = false;
  private suspended = false;
  private reconnectBlocked = false;
  private connectionWarned = false;
  private historyWarned = false;
  private lastSocketActivity = 0;
  private connected = false;
  private manualDisconnect = false;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private missedPongs = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempt = 0;
  private reconnectNeedsRefresh = false;
  private historyPollTimer: ReturnType<typeof setInterval> | null = null;
  private historyPollInFlight = false;
  private conversationId: number | null = null;
  private lastReceivedMid = 0;
  private ignoredSockets = new Set<ImSocket>();
  private seenMids = new Set<number>();

  configure(init: VocechatInit): void {
    this.init = { ...init, baseUrl: init.baseUrl.replace(/\/+$/, '') };
    this.manualDisconnect = false;
  }

  setLanguage(language: string): void {
    if (this.init && language) this.init.language = language;
  }

  setClientIp(ip: string): void {
    if (this.init && ip) this.init.clientIp = ip;
  }

  on(event: string, handler: Handler): () => void {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    this.listeners.get(event)!.add(handler);
    return () => this.off(event, handler);
  }

  off(event: string, handler: Handler): void {
    this.listeners.get(event)?.delete(handler);
  }

  emit(event: string, payload: unknown): void {
    if (event === 'visitor.message') {
      const text = (payload as { text?: string })?.text;
      if (text) {
        void this.sendText(text).catch((err) => dcsLog.warn('vocechat', '发送访客消息失败', err));
      }
    } else {
      dcsLog.warn('vocechat', `未识别的 emit 事件: ${event}`, payload);
    }
  }

  private dispatch(event: string, payload: unknown): void {
    this.listeners.get(event)?.forEach((handler) => handler(payload));
  }

  async connect(): Promise<void> {
    this.manualDisconnect = false;
    this.suspended = false;
    this.installLifecycle();
    if (this.connected && this.socket?.readyState === WebSocket.OPEN) return;
    if (this.connectPromise) return this.connectPromise;
    if (this.reconnectBlocked) return;
    const generation = this.generation;
    const connecting = this.connectCurrent(generation);
    this.connectPromise = connecting;
    try {
      await connecting;
    } finally {
      if (this.connectPromise === connecting) this.connectPromise = null;
    }
  }

  private async connectCurrent(generation: number): Promise<void> {
    let status: VocechatServiceStatus;
    try {
      status = await this.ensureStatus();
    } catch (error) {
      if (this.manualDisconnect || generation !== this.generation) return;
      throw error;
    }
    if (this.manualDisconnect || generation !== this.generation) return;
    this.dispatch(IM_EVENT.status, this.toImStatus(status));
    if (!status.online) {
      this.connected = true;
      return;
    }
    try {
      await this.openSocket();
      if (this.manualDisconnect || generation !== this.generation) return;
      this.stopHistoryPolling();
    } catch (err) {
      if (this.manualDisconnect || generation !== this.generation) return;
      if (err instanceof ImSocketError && !err.retryable) this.reconnectBlocked = true;
      this.warnConnectionFailure(err);
      this.startHistoryPolling();
      this.scheduleReconnect(false);
    }
    if (generation === this.generation && !this.manualDisconnect) this.connected = this.socket?.readyState === WebSocket.OPEN;
  }

  private warnConnectionFailure(error: unknown): void {
    if (this.connectionWarned) return;
    this.connectionWarned = true;
    dcsLog.warn('vocechat', '实时连接暂不可用，启用历史消息补偿', error);
  }

  disconnect(): void {
    this.manualDisconnect = true;
    this.generation += 1;
    this.connectPromise = null;
    this.recoveryPromise = null;
    this.historyRecoveryPromise = null;
    this.statusPromise = null;
    this.statusRefreshPromise = null;
    this.removeLifecycle();
    this.clearReconnectTimer();
    this.closeSocket();
    this.stopHistoryPolling();
    this.connected = false;
    this.listeners.clear();
  }

  getStatus(): ImStatus | null {
    return this.status ? this.toImStatus(this.status) : null;
  }

  async refreshServiceStatus(): Promise<void> {
    this.manualDisconnect = false;
    this.generation += 1;
    this.connectPromise = null;
    this.recoveryPromise = null;
    this.historyRecoveryPromise = null;
    this.statusRefreshPromise = null;
    this.reconnectBlocked = false;
    this.connectionWarned = false;
    this.clearReconnectTimer();
    this.closeSocket();
    this.stopHistoryPolling();
    this.status = null;
    this.statusPromise = null;
    this.connected = false;
    this.conversationId = null;
    this.lastReceivedMid = 0;
    this.seenMids.clear();
    await this.connect();
  }

  private async ensureStatus(): Promise<VocechatServiceStatus> {
    if (this.status) return this.status;
    if (this.statusPromise) return this.statusPromise;
    if (!this.init) {
      throw new Error('[vocechat] not configured — call configureImClient(...) before connect');
    }
    const promise = this.fetchStatus();
    this.statusPromise = promise;
    try {
      return await promise;
    } finally {
      if (this.statusPromise === promise) this.statusPromise = null;
    }
  }

  private async fetchStatus(): Promise<VocechatServiceStatus> {
    const generation = this.generation;
    const { baseUrl, agentId, visitorId, pageUrl, pageTitle, pageTitleProvider, language, clientIp, hostTokenProvider } = this.init!;
    const qs = new URLSearchParams({
      agent_id: agentId,
      visitor_id: visitorId,
      page_url: pageUrl,
      page_title: readPageTitle(pageTitleProvider, pageTitle),
      language,
    });
    if (clientIp) qs.set('client_ip', clientIp);
    const memberToken = hostTokenProvider?.()?.trim();
    const res = await fetch(`${baseUrl}/v1/service/status?${qs.toString()}`, {
      method: 'GET',
      ...(memberToken ? { headers: { 'X-Platform-Member-Token': memberToken } } : {}),
      credentials: 'omit',
    });
    const data = await this.unwrap<VocechatServiceStatus>(res);
    if (generation === this.generation) this.status = data;
    return data;
  }

  private async refreshStatusOnly(): Promise<VocechatServiceStatus> {
    if (this.statusRefreshPromise) return this.statusRefreshPromise;
    const generation = this.generation;
    const refreshing = (async () => {
      this.closeSocket();
      this.status = null;
      this.statusPromise = null;
      const status = await this.ensureStatus();
      if (generation === this.generation && !this.manualDisconnect) this.dispatch(IM_EVENT.status, this.toImStatus(status));
      return status;
    })();
    this.statusRefreshPromise = refreshing;
    try {
      return await refreshing;
    } finally {
      if (this.statusRefreshPromise === refreshing) this.statusRefreshPromise = null;
    }
  }

  private toImStatus(status: VocechatServiceStatus): ImStatus {
    return {
      online: status.online,
      agentName: status.agent?.agent_name ?? '',
      channelTypeLabel: status.agent?.channel_type_label ?? '',
      offline: status.online
        ? undefined
        : {
            guideText: status.offline_guide_text ?? null,
            qrUrl: status.offline_qr_url ?? null,
            serviceHours: status.service_hours ?? null,
          },
    };
  }

  private async openSocket(): Promise<void> {
    const wsUrl = this.status?.ws_url;
    const token = this.status?.visitor_token;
    if (!wsUrl || !token) throw new Error('[vocechat] online status missing ws_url or visitor_token');
    this.closeSocket();
    const url = new URL(wsUrl);
    url.searchParams.set('api-key', token);
    const socket = createImSocket(url.toString(), this.init?.socketBridgeUrl);
    this.socket = socket;

    await new Promise<void>((resolve, reject) => {
      let settled = false;
      const timeout = setTimeout(() => {
        if (settled) return;
        settled = true;
        reject(new ImSocketError('WebSocket 连接超时'));
        if (this.socket === socket) this.closeSocket();
      }, IM_SOCKET_TIMEOUT_MS);
      socket.onopen = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        this.connectionWarned = false;
        this.reconnectAttempt = 0;
        this.lastSocketActivity = Date.now();
        this.startHeartbeat(socket);
        resolve();
      };
      socket.onerror = (error) => {
        if (error instanceof ImSocketError && !error.retryable) this.reconnectBlocked = true;
        if (!settled) {
          settled = true;
          clearTimeout(timeout);
          reject(error ?? new ImSocketError('WebSocket connection failed'));
        }
      };
      socket.onmessage = (event) => {
        if (this.socket !== socket) return;
        this.lastSocketActivity = Date.now();
        this.handleSocketMessage(event.data);
      };
      socket.onclose = (event) => {
        clearTimeout(timeout);
        const ignored = this.ignoredSockets.delete(socket);
        if (this.socket === socket) this.stopHeartbeat();
        if (ignored) {
          if (!settled) {
            settled = true;
            reject(new Error('[vocechat] WebSocket closed by client'));
          }
          return;
        }
        if (!settled) {
          settled = true;
          reject(new Error(`[vocechat] WebSocket closed before open (${event.code})`));
        }
        if (this.socket !== socket) return;
        this.socket = null;
        this.connected = false;
        if (!this.manualDisconnect) {
          this.startHistoryPolling();
          this.scheduleReconnect([4002, 4003, 4004].includes(event.code));
        }
      };
    });
  }

  private startHeartbeat(socket: ImSocket): void {
    this.stopHeartbeat();
    this.missedPongs = 0;
    this.heartbeatTimer = setInterval(() => {
      if (socket.readyState !== WebSocket.OPEN) return;
      if (this.missedPongs >= 2) {
        this.missedPongs = 0;
        socket.close(4000, 'pong timeout');
        return;
      }
      this.missedPongs += 1;
      socket.send(JSON.stringify({ type: 'ping' }));
    }, PING_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
    this.missedPongs = 0;
  }

  private closeSocket(): void {
    this.stopHeartbeat();
    const socket = this.socket;
    this.socket = null;
    if (socket && socket.readyState < WebSocket.CLOSING) {
      this.ignoredSockets.add(socket);
      socket.close(1000, 'client refresh');
    }
  }

  private handleSocketMessage(raw: unknown): void {
    if (typeof raw !== 'string') return;
    let frame: VocechatWsFrame;
    try {
      frame = JSON.parse(raw) as VocechatWsFrame;
    } catch (err) {
      dcsLog.warn('vocechat', '解析 WebSocket 帧失败', err);
      return;
    }
    const data = (frame.data ?? {}) as Record<string, unknown>;
    switch (frame.type) {
      case 'pong':
        this.missedPongs = 0;
        return;
      case 'im:ready':
        if (typeof data.conversation_id === 'number') this.conversationId = data.conversation_id;
        this.reconnectAttempt = 0;
        this.dispatch('im.ready', data);
        return;
      case 'im:message': {
        const payload = frame.data as VocechatWsMessageData;
        if (typeof payload?.conversation_id === 'number') this.conversationId = payload.conversation_id;
        if (typeof payload?.mid === 'number') this.lastReceivedMid = Math.max(this.lastReceivedMid, payload.mid);
        if (typeof payload?.mid === 'number' && this.seenMids.has(payload.mid)) return;
        if (typeof payload?.mid === 'number') this.rememberMid(payload.mid);
        if (payload?.message) this.dispatch(IM_EVENT.message, this.normalizeMessage(payload.message, payload));
        return;
      }
      case 'im:typing':
        if (typeof data.conversation_id === 'number') this.conversationId = data.conversation_id;
        if (data.from === 'agent') this.dispatch(IM_EVENT.typing, { ...data, isTyping: data.is_typing === true });
        return;
      case 'im:read':
        if (typeof data.conversation_id === 'number') this.conversationId = data.conversation_id;
        if (data.reader === 'agent') this.dispatch(IM_EVENT.read, data);
        return;
      case 'im:conversation-changed':
        if (typeof data.conversation_id === 'number') this.conversationId = data.conversation_id;
        this.dispatch(IM_EVENT.conversationChanged, data);
        return;
      case 'im:unread-changed':
        this.dispatch(IM_EVENT.unreadChanged, data);
        return;
      case 'im:session-expired':
        this.dispatch(IM_EVENT.sessionExpired, data);
        this.scheduleReconnect(true);
        return;
      default:
        return;
    }
  }

  private normalizeMessage(m: VocechatHistoryMessage, envelope?: VocechatWsMessageData): ImIncomingMessage {
    const msgType = getVocechatMessageType(m.content_type);
    const senderRole = m.sender_role;
    return {
      mid: m.mid,
      seq: envelope?.seq,
      conversationId: envelope?.conversation_id,
      sessionId: m.session_id,
      clientMsgId: m.client_msg_id,
      fromUid: m.from_uid,
      senderRole,
      isVisitor: senderRole === 'visitor',
      msgType,
      text: msgType === 'text' ? m.content : m.file_name ?? m.content,
      fileKey: msgType === 'text' ? undefined : m.file_key ?? m.content,
      fileMime: m.file_mime,
      fileSize: m.file_size,
      fileName: m.file_name,
      ts: messageTimestamp(m.created_at),
    };
  }

  private scheduleReconnect(needsRefresh: boolean): void {
    this.reconnectNeedsRefresh = this.reconnectNeedsRefresh || needsRefresh;
    if (this.manualDisconnect || this.suspended || this.reconnectBlocked || this.reconnectTimer) return;
    const delay = Math.min(RECONNECT_MAX_MS, 1000 * 2 ** this.reconnectAttempt);
    this.reconnectAttempt += 1;
    const generation = this.generation;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.reconnect().catch((err) => {
        if (this.manualDisconnect || this.suspended || generation !== this.generation) return;
        this.warnConnectionFailure(err);
        this.scheduleReconnect(this.reconnectNeedsRefresh);
      });
    }, delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.reconnectNeedsRefresh = false;
  }

  private startHistoryPolling(): void {
    if (this.manualDisconnect || this.suspended || this.historyPollTimer) return;
    this.historyPollTimer = setInterval(() => {
      void this.pollHistory().catch((err) => this.warnHistoryFailure(err));
    }, HISTORY_POLL_INTERVAL_MS);
    void this.pollHistory().catch((err) => this.warnHistoryFailure(err));
  }

  private warnHistoryFailure(error: unknown): void {
    if (this.historyWarned || this.manualDisconnect || this.suspended) return;
    this.historyWarned = true;
    dcsLog.warn('vocechat', '历史消息补偿暂不可用', error);
  }

  private stopHistoryPolling(): void {
    if (this.historyPollTimer) clearInterval(this.historyPollTimer);
    this.historyPollTimer = null;
  }

  private async pollHistory(): Promise<void> {
    if (this.manualDisconnect || this.suspended || this.historyPollInFlight || !this.status?.online) return;
    if (!this.status.session_id) return;
    this.historyPollInFlight = true;
    try {
      await this.recoverHistory(this.lastReceivedMid);
      this.historyWarned = false;
    } finally {
      this.historyPollInFlight = false;
    }
  }

  private async reconnect(): Promise<void> {
    if (this.manualDisconnect) return;
    const generation = this.generation;
    // 在打开新连接前固定补偿起点，避免新推送先到而把断线期间的旧消息跳过。
    const checkpoint = this.lastReceivedMid;
    const needsRefresh = this.reconnectNeedsRefresh;
    this.reconnectNeedsRefresh = false;
    if (needsRefresh) await this.refreshStatusOnly();
    if (this.manualDisconnect || this.suspended || generation !== this.generation) return;
    await this.connect();
    if (this.manualDisconnect || this.suspended || generation !== this.generation) return;
    if (this.status?.session_id && checkpoint > 0) await this.recoverHistory(checkpoint);
  }

  private async recoverHistory(checkpoint: number): Promise<void> {
    if (this.historyRecoveryPromise) return this.historyRecoveryPromise;
    const generation = this.generation;
    const recovering = (async () => {
      const recovered = new Map<number, ImIncomingMessage>();
      let beforeMid: number | undefined;
      while (!this.manualDisconnect && !this.suspended && generation === this.generation) {
        // 从最新页向前翻到断线起点，避免一次 limit=100 截断大量离线消息。
        const page = await this.fetchHistoryPage({ beforeMid, limit: 100 });
        if (this.manualDisconnect || this.suspended || generation !== this.generation) return;
        page.messages.forEach((message) => {
          if (message.mid > checkpoint) recovered.set(message.mid, message);
        });
        const oldest = page.messages[0]?.mid;
        const next = page.nextBeforeMid;
        if (checkpoint === 0 || !page.hasMore || oldest == null || oldest <= checkpoint || next == null || (beforeMid != null && next >= beforeMid)) break;
        beforeMid = next;
      }
      if (this.manualDisconnect || this.suspended || generation !== this.generation) return;
      [...recovered.values()].sort((a, b) => a.mid - b.mid).forEach((message) => {
        // 查询期间 WebSocket 可能已经回显；提交时再查一次去重集合。
        if (this.seenMids.has(message.mid)) return;
        this.lastReceivedMid = Math.max(this.lastReceivedMid, message.mid);
        this.rememberMid(message.mid);
        this.dispatch(IM_EVENT.message, message);
      });
    })();
    this.historyRecoveryPromise = recovering;
    try {
      await recovering;
    } finally {
      if (this.historyRecoveryPromise === recovering) this.historyRecoveryPromise = null;
    }
  }

  private installLifecycle(): void {
    if (this.lifecycleInstalled) return;
    this.lifecycleInstalled = true;
    window.addEventListener('pagehide', this.suspendConnection);
    window.addEventListener('pageshow', this.resumeConnection);
    window.addEventListener('online', this.resumeConnection);
    window.addEventListener('dcs:im-page-active', this.resumeConnection);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    document.addEventListener('WeixinJSBridgeReady', installMiniProgramLifecycle);
    installMiniProgramLifecycle();
  }

  private removeLifecycle(): void {
    if (!this.lifecycleInstalled) return;
    this.lifecycleInstalled = false;
    window.removeEventListener('pagehide', this.suspendConnection);
    window.removeEventListener('pageshow', this.resumeConnection);
    window.removeEventListener('online', this.resumeConnection);
    window.removeEventListener('dcs:im-page-active', this.resumeConnection);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    document.removeEventListener('WeixinJSBridgeReady', installMiniProgramLifecycle);
  }

  private suspendConnection = (): void => {
    this.suspended = true;
    this.generation += 1;
    this.connectPromise = null;
    this.recoveryPromise = null;
    this.historyRecoveryPromise = null;
    this.statusPromise = null;
    this.statusRefreshPromise = null;
    this.clearReconnectTimer();
    this.closeSocket();
    this.stopHistoryPolling();
    this.connected = false;
  };

  private onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') this.resumeConnection();
  };

  private resumeConnection = (event?: Event): void => {
    if (this.manualDisconnect || !this.init || this.reconnectBlocked || this.recoveryPromise || this.connectPromise) return;
    const force = event?.type === 'online' || event?.type === 'dcs:im-page-active';
    if (!force && !this.suspended && this.socket?.readyState === WebSocket.OPEN && Date.now() - this.lastSocketActivity < PING_INTERVAL_MS) return;
    this.suspended = false;
    this.clearReconnectTimer();
    this.reconnectNeedsRefresh = true;
    const generation = this.generation;
    const recovery = this.reconnect().catch((err) => {
      if (this.manualDisconnect || generation !== this.generation) return;
      this.warnConnectionFailure(err);
      this.startHistoryPolling();
      this.scheduleReconnect(true);
    }).finally(() => {
      if (this.recoveryPromise === recovery) this.recoveryPromise = null;
    });
    this.recoveryPromise = recovery;
  };

  private rememberMid(mid: number): void {
    this.seenMids.add(mid);
    if (this.seenMids.size > 5000) {
      const oldest = this.seenMids.values().next().value as number | undefined;
      if (oldest != null) this.seenMids.delete(oldest);
    }
  }

  async sendText(text: string, clientMsgId = makeClientMsgId()): Promise<number | null> {
    const trimmed = text.trim();
    if (!trimmed) return null;
    const result = await this.authedJson<VocechatSendResult>('/v1/visitor/send', {
      content: trimmed,
      language: this.init?.language,
      client_msg_id: clientMsgId,
    });
    return result.mid;
  }

  async sendFile(file: File, clientMsgId = makeClientMsgId()): Promise<number | null> {
    const uploaded = await this.uploadFile(file);
    const result = await this.authedJson<VocechatSendResult>('/v1/visitor/send-file', {
      key: uploaded.key,
      mime: uploaded.mime,
      size: uploaded.size,
      filename: file.name,
      language: this.init?.language,
      client_msg_id: clientMsgId,
    });
    return result.mid;
  }

  private async uploadFile(file: File): Promise<VocechatUploadResult> {
    return this.withAuthRetry(async () => {
      const token = await this.sessionToken();
      const form = new FormData();
      form.append('file', file);
      form.append('filename', file.name);
      if (file.type) form.append('mime_type', file.type);
      const res = await fetch(`${this.init!.baseUrl}/v1/files/visitor/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
        credentials: 'omit',
      });
      const body = await this.readEnvelope(res);
      return { key: body.key as string, mime: body.mime as string, size: body.size as number };
    });
  }

  async getViewUrl(key: string): Promise<string> {
    return this.withAuthRetry(async () => {
      const res = await fetch(`${this.init!.baseUrl}/v1/files/visitor/view-url?key=${encodeURIComponent(key)}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${await this.sessionToken()}` },
        credentials: 'omit',
      });
      const body = await this.readEnvelope(res);
      return (body as unknown as VocechatViewUrlResult).view_url;
    });
  }

  async loadHistory(opts: { beforeMid?: number; afterMid?: number; limit?: number } = {}): Promise<{
    messages: ImIncomingMessage[];
    hasMore: boolean;
    nextBeforeMid: number | null;
  }> {
    const generation = this.generation;
    const page = await this.fetchHistoryPage(opts);
    if (generation !== this.generation) return { messages: [], hasMore: false, nextBeforeMid: null };
    page.messages.forEach((message) => {
      this.lastReceivedMid = Math.max(this.lastReceivedMid, message.mid);
      this.rememberMid(message.mid);
      if (message.conversationId != null) this.conversationId = message.conversationId;
    });
    return page;
  }

  private async fetchHistoryPage(opts: { beforeMid?: number; afterMid?: number; limit?: number }): Promise<{
    messages: ImIncomingMessage[];
    hasMore: boolean;
    nextBeforeMid: number | null;
  }> {
    const status = await this.ensureStatus();
    if (!status.session_id) return { messages: [], hasMore: false, nextBeforeMid: null };
    if (opts.beforeMid != null && opts.afterMid != null) throw new Error('[vocechat] beforeMid and afterMid cannot both be set');
    const result = await this.withAuthRetry(async () => {
      const qs = new URLSearchParams({ session_id: this.status?.session_id ?? status.session_id!, limit: String(opts.limit ?? 20) });
      if (opts.beforeMid != null) qs.set('before_mid', String(opts.beforeMid));
      if (opts.afterMid != null) qs.set('after_mid', String(opts.afterMid));
      const res = await fetch(`${this.init!.baseUrl}/v1/messages/history?${qs.toString()}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${await this.sessionToken()}` },
        credentials: 'omit',
      });
      return this.unwrap<VocechatHistoryResult>(res);
    });
    const messages = result.messages
      .map((message) => this.normalizeMessage(message))
      .sort((a, b) => a.mid - b.mid);
    return {
      messages,
      hasMore: result.has_more,
      nextBeforeMid: result.next_before_mid ?? (result.has_more && messages.length ? messages[0].mid : null),
    };
  }

  async sendTyping(isTyping: boolean): Promise<void> {
    if (this.conversationId == null) return;
    try {
      await this.authedJson('/v1/typing', { conversation_id: this.conversationId, is_typing: isTyping });
    } catch (err) {
      dcsLog.warn('vocechat', 'POST /typing 失败', err);
    }
  }

  async sendRead(lastReadMid?: number): Promise<void> {
    if (this.conversationId == null || lastReadMid == null || lastReadMid <= 0) return;
    try {
      await this.authedJson('/v1/read', { conversation_id: this.conversationId, last_read_mid: lastReadMid });
    } catch (err) {
      dcsLog.warn('vocechat', 'POST /read 失败', err);
    }
  }

  private async sessionToken(): Promise<string> {
    const status = await this.ensureStatus();
    if (!status.visitor_token) throw new Error('[vocechat] no visitor_token (offline?)');
    return status.visitor_token;
  }

  private async authedJson<T = unknown>(path: string, body: unknown): Promise<T> {
    return this.withAuthRetry(async () => {
      const res = await fetch(`${this.init!.baseUrl}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await this.sessionToken()}` },
        body: JSON.stringify(body),
        credentials: 'omit',
      });
      return this.unwrap<T>(res);
    });
  }

  private async withAuthRetry<T>(fn: () => Promise<T>): Promise<T> {
    try {
      return await fn();
    } catch (err) {
      if (!isAuthError(err)) throw err;
      dcsLog.warn('vocechat', 'visitor_token 失效,重新获取后重试');
      await this.refreshStatusOnly();
      if (!this.manualDisconnect && !this.suspended && this.status?.online && (!this.socket || this.socket.readyState !== WebSocket.OPEN)) {
        void this.connect().catch((connectError) => dcsLog.warn('vocechat', 'token 刷新后 WebSocket 重连失败', connectError));
      }
      return fn();
    }
  }

  private async readEnvelope(res: Response): Promise<Record<string, unknown>> {
    const text = await res.text();
    let body: Record<string, unknown>;
    try {
      body = text ? (JSON.parse(text) as Record<string, unknown>) : {};
    } catch {
      throw new HttpCodeError(res.status, null, `non-json: ${text.slice(0, 80)}`);
    }
    if (!res.ok || (body.code != null && body.code !== 0)) {
      throw new HttpCodeError(
        res.status,
        (body.code as number | string) ?? null,
        `${res.status}: ${(body.message as string) ?? 'request failed'}`,
      );
    }
    return body;
  }

  private async unwrap<T>(res: Response): Promise<T> {
    const body = await this.readEnvelope(res);
    return (body.data ?? body) as T;
  }
}

let instance: VocechatTransportClient | null = null;

export function getImClient(): VocechatTransportClient {
  if (!instance) instance = new VocechatTransportClient();
  return instance;
}

export function configureImClient(init: VocechatInit): void {
  getImClient().configure(init);
}

export function setImClientLanguage(language: string): void {
  getImClient().setLanguage(language);
}

export function setImClientClientIp(ip: string): void {
  getImClient().setClientIp(ip);
}

export function resetImClient(): void {
  if (instance) instance.disconnect();
  instance = null;
}
